/**
 * Reading the log into this browser's copy (log-kit/store.ts), a page of 100 per Torn call, with the player's own key
 * (Full Access), on the page's open or the player's click — never on a timer:
 *  - `fromServer`: what the Omni-Torn server keeps for you, no Torn call at all — from terms 19 everything (every
 *    category, from every device, background tracking and the server's backfill while you're away), caught up from where
 *    this copy stopped, with the stretches it knows complete; below 19 what background tracking kept (90 days);
 *  - `readNewer`: from now back until it meets what's kept (or the page limit) — nothing when the server's copy is fresh;
 *  - `readOlder`: on from the oldest end of the newest unbroken stretch, until Torn has nothing older.
 * From terms 19 every Torn page read is sent to the server with the stretch it covered (app/own-history.ts), so it is
 * never read twice. A stream is the whole log or one log category (v1 `cat`); each keeps its own record of what was read
 * in full. It leaves `spare` of the site's calls this minute for the other pages, and stops there.
 */
import { intersectSpans, newestSpan, olderCursor, type Span } from '@omni-torn/shared';
import { ownHistoryOn, ownRead, ownSend } from '../own-history';
import { readLogs, type LogEntry } from '../read';
import { server, torn, tornCallsLeft } from '../session';
import type { LogStore } from './store';

/** The server's copy is this fresh (its newest stretch reaches this close to now): no Torn read for newer lines. */
const FRESH_MS = 2 * 60_000;
/** Lines per server read when catching this copy up, and reads per catch-up (the rest on the next open or click). */
const SYNC_PAGE = 2_000;

export interface LogStream {
  /** Name the read stretches are kept under ("all", "cat:136"). */
  id: string;
  /** Torn's log category id (v1 `cat`), when the stream is one category. */
  cat?: number | null;
  /** Which kept lines belong to it (the server's copy has every category). */
  keep?: (e: LogEntry) => boolean;
}

export interface ReadResult {
  added: number;
  pages: number;
  /** Why it stopped: met what's kept / Torn has no more, the page limit, or this minute's share of calls. */
  stop: 'met' | 'start' | 'pages' | 'budget';
}

/** Torn's page size for the log. */
const PAGE = 100;

/** What a report page and the kit's panel use of a reader (one stream, or several read together: `MultiReader`). */
export interface LogReaderLike {
  spans(): Promise<Span[]>;
  complete(): Promise<boolean>;
  readNewer(maxPages?: number): Promise<ReadResult>;
  readOlder(maxPages?: number): Promise<ReadResult>;
  fromServer(maxRounds?: number): Promise<number>;
}

export class LogReader implements LogReaderLike {
  constructor(
    private readonly store: LogStore,
    readonly stream: LogStream,
    /** Calls to leave for the other pages this minute. */
    private readonly spare = 3,
  ) {}

  spans(): Promise<Span[]> {
    return this.store.spans(this.stream.id);
  }

  /** Has Torn said there is nothing older (the stream's whole log is kept)? */
  async complete(): Promise<boolean> {
    return (await this.spans())[0]?.from === 0;
  }

  async #page(toSec: number | null): Promise<{ got: LogEntry[]; raw: Record<string, unknown> }> {
    const params: Record<string, string> = { selections: 'log' };
    if (this.stream.cat) params.cat = String(this.stream.cat);
    if (toSec !== null) params.to = String(toSec);
    const body = await torn('/user/', params, toSec === null ? 30_000 : 600_000);
    const raw = body && typeof body === 'object' && (body as { log?: unknown }).log && typeof (body as { log?: unknown }).log === 'object' ? ((body as { log: Record<string, unknown> }).log) : {};
    return { got: readLogs(body).filter((e) => !this.stream.keep || this.stream.keep(e)), raw };
  }

  /** One Torn page to the server, with the stretch it covered in full (terms 19; one upload per read). */
  async #send(raw: Record<string, unknown>, span: Span): Promise<void> {
    if (!ownHistoryOn()) return;
    // A stream that keeps only some lines of a page (no category to ask Torn for): what it calls "nothing more" may be a
    // full page of other lines — the server is told only what the page itself shows.
    const times = Object.values(raw).map((e) => Number((e as { timestamp?: unknown } | null)?.timestamp) * 1000).filter((t) => Number.isFinite(t) && t > 0);
    const low = times.length ? Math.min(...times) : null;
    const claim = low !== null && times.length >= PAGE && span.from < low ? { from: low, to: span.to } : span;
    await ownSend('log', raw, claim, this.stream.cat ?? null);
  }

  #canCall(): boolean {
    return tornCallsLeft() > this.spare;
  }

  /** Newest first, back to what's kept. */
  async readNewer(maxPages = 5): Promise<ReadResult> {
    const now = Date.now();
    const newest = newestSpan(await this.spans());
    // The server's copy (terms 19) already reaches now: nothing newer to ask Torn for.
    if (ownHistoryOn() && newest && newest.to >= now - FRESH_MS) return { added: 0, pages: 0, stop: 'met' };
    let added = 0;
    let pages = 0;
    let toSec: number | null = null;
    let oldest = now;
    /** Where the page just read began (its stretch goes up to here). */
    let upper = now;
    let stop: ReadResult['stop'] = 'pages';
    while (pages < maxPages) {
      if (!this.#canCall()) {
        stop = 'budget';
        break;
      }
      const { got, raw } = await this.#page(toSec);
      pages++;
      const fresh = await this.store.put(got);
      added += fresh;
      if (!got.length) {
        // Nothing at all from here back: the whole stream is read.
        oldest = 0;
        stop = 'start';
        await this.#send(raw, { from: 0, to: upper });
        break;
      }
      const low = Math.min(...got.map((e) => e.at));
      oldest = Math.min(oldest, low);
      if (newest && low <= newest.to) {
        stop = 'met';
        await this.#send(raw, { from: low, to: upper });
        break;
      }
      // A page that brought nothing new can't go on from the same second.
      toSec = Math.floor(low / 1000) - (fresh ? 0 : 1);
      if (got.length < PAGE && fresh === 0) {
        stop = 'start';
        oldest = 0;
        await this.#send(raw, { from: 0, to: upper });
        break;
      }
      await this.#send(raw, { from: low, to: upper });
      upper = low;
    }
    if (pages) await this.store.addSpan(this.stream.id, { from: stop === 'met' ? Math.min(oldest, newest!.to) : oldest, to: now });
    return { added, pages, stop };
  }

  /** On back in time from the newest unbroken stretch. */
  async readOlder(maxPages = 5): Promise<ReadResult> {
    let cursor = olderCursor(await this.spans());
    if (cursor === null) return this.readNewer(maxPages);
    if (cursor === 0) return { added: 0, pages: 0, stop: 'start' };
    let added = 0;
    let pages = 0;
    let stop: ReadResult['stop'] = 'pages';
    let toSec = Math.floor(cursor / 1000);
    while (pages < maxPages) {
      if (!this.#canCall()) {
        stop = 'budget';
        break;
      }
      const { got, raw } = await this.#page(toSec);
      pages++;
      const fresh = await this.store.put(got);
      added += fresh;
      const low = got.length ? Math.min(...got.map((e) => e.at)) : 0;
      if (!got.length || (fresh === 0 && got.length < PAGE)) {
        await this.store.addSpan(this.stream.id, { from: 0, to: cursor });
        await this.#send(raw, { from: 0, to: cursor });
        stop = 'start';
        break;
      }
      await this.#send(raw, { from: low, to: cursor });
      const spans = await this.store.addSpan(this.stream.id, { from: low, to: cursor });
      cursor = low;
      // Joined an older stretch already kept: carry on from its start.
      const next = olderCursor(spans);
      if (next !== null && next < low) {
        cursor = next;
        if (next === 0) {
          stop = 'start';
          break;
        }
      }
      toSec = Math.floor(cursor / 1000) - (fresh ? 0 : 1);
    }
    return { added, pages, stop };
  }

  /**
   * What the server keeps (no Torn call). Answers how many lines were new here. Terms 19: this copy catches up from where
   * it stopped (every category — the copy is one per player) and takes the stream's stretches the server knows complete;
   * below 19: what background tracking kept (all of it, 500 at a time).
   */
  async fromServer(maxRounds = 10): Promise<number> {
    if (ownHistoryOn()) return this.#catchUp(maxRounds * 2);
    let added = 0;
    let before: number | null = null;
    for (let i = 0; i < maxRounds; i++) {
      const kept: { log: Record<string, unknown> } | null = await server<{ log: Record<string, unknown> }>('GET', `/api/me/logs?limit=500${before ? `&before=${before}` : ''}`).catch(() => null);
      const got = readLogs({ log: kept?.log ?? {} });
      if (!got.length) break;
      added += await this.store.put(got.filter((e) => !this.stream.keep || this.stream.keep(e)));
      before = Math.min(...got.map((e) => e.at));
      if (got.length < 500) break;
    }
    return added;
  }

  async #catchUp(maxRounds: number): Promise<number> {
    let added = 0;
    let since = await this.store.seq('log');
    let spans: Span[] | null = null;
    for (let i = 0; i < maxRounds; i++) {
      const r = await ownRead('log', { since, limit: SYNC_PAGE, cat: this.stream.cat ?? null }).catch(() => null);
      if (!r?.on) break;
      spans ??= r.spans;
      // The server's copy was deleted since (Delete my data): start again from its beginning.
      if (since > r.last) {
        since = 0;
        continue;
      }
      const got = readLogs({ log: r.entries });
      added += await this.store.put(got);
      since = r.seq;
      await this.store.setSeq('log', since);
      if (!r.more) break;
    }
    for (const s of spans ?? []) await this.store.addSpan(this.stream.id, s);
    return added;
  }
}

/**
 * Several streams read as one (a report over two of Torn's log categories — S26's Item Market and Bazaar): each read
 * goes through every stream in turn (each keeps to the minute's share itself); "read in full" is what ALL of them have
 * read. The server's copy is fetched once (it holds every category).
 */
export class MultiReader implements LogReaderLike {
  constructor(private readonly readers: readonly LogReader[]) {}

  async spans(): Promise<Span[]> {
    return intersectSpans(await Promise.all(this.readers.map((r) => r.spans())));
  }

  async complete(): Promise<boolean> {
    return (await Promise.all(this.readers.map((r) => r.complete()))).every(Boolean);
  }

  async #each(read: (r: LogReader) => Promise<ReadResult>, skipComplete: boolean): Promise<ReadResult> {
    const out: ReadResult = { added: 0, pages: 0, stop: 'start' };
    const rank: Record<ReadResult['stop'], number> = { start: 0, met: 1, pages: 2, budget: 3 };
    for (const r of this.readers) {
      if (skipComplete && (await r.complete())) continue;
      const got = await read(r);
      out.added += got.added;
      out.pages += got.pages;
      if (rank[got.stop] > rank[out.stop]) out.stop = got.stop;
      if (got.stop === 'budget') break;
    }
    return out;
  }

  readNewer(maxPages = 5): Promise<ReadResult> {
    return this.#each((r) => r.readNewer(maxPages), false);
  }

  readOlder(maxPages = 5): Promise<ReadResult> {
    return this.#each((r) => r.readOlder(maxPages), true);
  }

  /** The server's copy: once (it holds every category) — from terms 19 each stream also takes its own stretches (one light read each). */
  async fromServer(maxRounds = 10): Promise<number> {
    if (!this.readers[0]) return 0;
    let added = await this.readers[0].fromServer(maxRounds);
    if (ownHistoryOn()) for (const r of this.readers.slice(1)) added += await r.fromServer(maxRounds);
    return added;
  }
}

/**
 * A report's first look at its stream on the page's open: what the server kept (no Torn call), then Torn's newest pages
 * back to what's kept. Answers the note to show (a failed Torn read becomes the note's problem, not the page's).
 */
export async function firstRead(r: LogReaderLike, pages = 3): Promise<string> {
  await r.fromServer().catch(() => 0);
  return readNote(await r.readNewer(pages));
}

/** Plain words for a read that stopped. */
export function readNote(r: ReadResult): string {
  const n = `${r.added.toLocaleString('en-US')} new`;
  if (r.stop === 'start') return `${n} — that’s everything Torn has.`;
  if (r.stop === 'budget') return `${n} — this minute’s share of your Torn calls is used; carry on in a minute.`;
  if (r.stop === 'met') return `${n} — up to date.`;
  return `${n} — read ${r.pages} page${r.pages === 1 ? '' : 's'}; there’s more.`;
}

